'use client';

import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ButtonV2 } from '@/components/ui/ButtonV2';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'warning' | 'error' | 'info';
}

const toastIcons = {
  success: CheckCircleIcon,
  error: ExclamationCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

// Theme-consistent glass variants + colors
const toastStyles = {
  success: 'liquid-glass glass-success border border-border/30',
  error: 'liquid-glass glass-error border border-border/30',
  warning: 'liquid-glass glass-warning border border-border/30',
  info: 'liquid-glass glass-suggestion border border-border/30',
};

const toastTextColors = {
  success: 'text-foreground',
  error: 'text-foreground',
  warning: 'text-foreground',
  info: 'text-foreground',
};

const toastIconColors = {
  success: 'text-success',
  error: 'text-destructive',
  warning: 'text-amber-500 dark:text-amber-400',
  info: 'text-primary',
};

// Global toast state
let toastListeners: ((toasts: Toast[]) => void)[] = [];
let toasts: Toast[] = []; // currently visible toasts
let toastQueue: Toast[] = []; // queued toasts not yet shown
const MAX_VISIBLE = 3;

// Global confirm state
let confirmListeners: ((confirm: { id: string; options: ConfirmOptions; resolve: (result: boolean) => void } | null) => void)[] = [];
let currentConfirm: { id: string; options: ConfirmOptions; resolve: (result: boolean) => void } | null = null;

export const toast = {
  success: (message: string, duration = 3000) => addToast({ message, type: 'success', duration }),
  error: (message: string, duration = 5000) => addToast({ message, type: 'error', duration }),
  warning: (message: string, duration = 4000) => addToast({ message, type: 'warning', duration }),
  info: (message: string, duration = 3000) => addToast({ message, type: 'info', duration }),
};

export const confirm = (options: string | ConfirmOptions): Promise<boolean> => {
  return new Promise((resolve) => {
    const confirmOptions: ConfirmOptions = typeof options === 'string' 
      ? { message: options }
      : options;
    
    const id = Math.random().toString(36).substr(2, 9);
    currentConfirm = { id, options: confirmOptions, resolve };
    confirmListeners.forEach(listener => listener(currentConfirm));
  });
};

export const alert = (message: string, type: ToastType = 'info') => {
  toast[type](message);
};

function addToast(toastInput: Omit<Toast, 'id'>) {
  const id = Math.random().toString(36).substr(2, 9);
  const newToast: Toast = { ...toastInput, id };

  if (toasts.length < MAX_VISIBLE) {
    toasts = [...toasts, newToast];
    toastListeners.forEach((listener) => listener(toasts));
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => removeToast(id), newToast.duration);
    }
  } else {
    // queue without timer; it will get a timer when promoted to visible
    toastQueue = [...toastQueue, newToast];
  }
}

function removeToast(id: string) {
  toasts = toasts.filter(t => t.id !== id);
  // Promote next item from queue if available
  if (toastQueue.length > 0) {
    const next = toastQueue.shift()!;
    toasts = [...toasts, next];
    // Start its timer now
    if (next.duration && next.duration > 0) {
      setTimeout(() => removeToast(next.id), next.duration);
    }
  }
  toastListeners.forEach(listener => listener(toasts));
}

function resolveConfirm(result: boolean) {
  if (currentConfirm) {
    currentConfirm.resolve(result);
    currentConfirm = null;
    confirmListeners.forEach(listener => listener(null));
  }
}

export function Toaster() {
  const [localToasts, setLocalToasts] = useState<Toast[]>([]);
  const [localConfirm, setLocalConfirm] = useState<{ id: string; options: ConfirmOptions; resolve: (result: boolean) => void } | null>(null);

  useEffect(() => {
    const listener = (newToasts: Toast[]) => setLocalToasts(newToasts);
    toastListeners.push(listener);
    return () => {
      toastListeners = toastListeners.filter(l => l !== listener);
    };
  }, []);

  useEffect(() => {
    const listener = (confirm: { id: string; options: ConfirmOptions; resolve: (result: boolean) => void } | null) => setLocalConfirm(confirm);
    confirmListeners.push(listener);
    return () => {
      confirmListeners = confirmListeners.filter(l => l !== listener);
    };
  }, []);

  const handleConfirm = (result: boolean) => {
    resolveConfirm(result);
  };

  const getConfirmIcon = (type?: string) => {
    switch (type) {
      case 'error':
        return ExclamationCircleIcon;
      case 'warning':
        return ExclamationTriangleIcon;
      default:
        return InformationCircleIcon;
    }
  };

  const getConfirmIconStyle = (type?: string) => {
    switch (type) {
      case 'error':
        return 'text-destructive';
      case 'warning':
        return 'text-warning';
      default:
        return 'text-primary';
    }
  };

  const getConfirmIconBg = (type?: string) => {
    switch (type) {
      case 'error':
        return 'bg-destructive/10';
      case 'warning':
        return 'bg-warning/10';
      default:
        return 'bg-primary/10';
    }
  };

  return (
    <>
      {/* Toast Notifications */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-3 max-w-sm pointer-events-none">
        {localToasts.map((toast, index) => {
          const Icon = toastIcons[toast.type];
          const translate = Math.min(index * 4, 16); // cap offset
          const scale = Math.max(1 - index * 0.02, 0.94);
          const opacity = Math.max(1 - index * 0.06, 0.82);
          return (
            <div
              key={toast.id}
              className={`flex items-start gap-3 p-4 rounded-xl animate-slide-up shadow-lg transition-all duration-300 ${toastStyles[toast.type]} pointer-events-auto`}
              style={{
                transform: `translateY(-${translate}px) scale(${scale})`,
                opacity,
                zIndex: 1000 + (localToasts.length - index),
              }}
            >
              <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${toastIconColors[toast.type]}`} />
              <p className={`flex-1 text-sm font-medium ${toastTextColors[toast.type]}`}>{toast.message}</p>
              <button
                onClick={() => removeToast(toast.id)}
                className="flex-shrink-0 hover:opacity-70 transition-opacity text-muted-foreground"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Confirmation Modal */}
      {localConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
            onClick={() => handleConfirm(false)}
          />

          {/* Modal */}
          <div className="relative bg-background/95 backdrop-blur-xl border border-border/40 rounded-2xl max-w-sm w-full mx-4 shadow-2xl animate-scale-in overflow-hidden">
            {/* Header */}
            <div className="px-5 pt-5 pb-0 flex items-start gap-3">
              {(() => {
                const Icon = getConfirmIcon(localConfirm.options.type);
                return (
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${getConfirmIconBg(localConfirm.options.type)}`}>
                    <Icon className={`w-5 h-5 ${getConfirmIconStyle(localConfirm.options.type)}`} />
                  </div>
                );
              })()}
              <div className="flex-1 min-w-0 pt-1">
                {localConfirm.options.title && (
                  <h3 className="text-sm font-semibold text-foreground leading-tight">
                    {localConfirm.options.title}
                  </h3>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="px-5 pt-3 pb-5">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {localConfirm.options.message}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end px-5 py-4 border-t border-border/30 bg-muted/20">
              <ButtonV2
                variant="ghost"
                size="sm"
                onClick={() => handleConfirm(false)}
              >
                {localConfirm.options.cancelText || 'Cancel'}
              </ButtonV2>

              <ButtonV2
                variant={localConfirm.options.type === 'error' ? 'destructive' : 'primary'}
                size="sm"
                onClick={() => handleConfirm(true)}
              >
                {localConfirm.options.confirmText || 'Confirm'}
              </ButtonV2>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
